import { supabase, isSupabaseConfigured } from '../supabase-client.ts';

const SUPABASE_CONFIG_ERROR =
  'Supabase is not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in ui/.env and restart npm start.';

const hybridAuthService = {
  // Store session data in localStorage
  setSessionData(sessionData) {
    const now = Date.now();
    localStorage.setItem('sessionData', JSON.stringify(sessionData));
    localStorage.setItem('sessionStartTime', now.toString());
    localStorage.setItem('lastActivityTime', now.toString());
  },

  // Get session data from localStorage
  getSessionData() {
    const data = localStorage.getItem('sessionData');
    if (!data) {
      return null;
    }

    try {
      return JSON.parse(data);
    } catch (error) {
      this.removeSessionData();
      return null;
    }
  },

  // Get session start time
  getSessionStartTime() {
    return localStorage.getItem('sessionStartTime');
  },

  // Get last activity time
  getLastActivityTime() {
    return localStorage.getItem('lastActivityTime');
  },

  // Update last activity time
  updateLastActivityTime() {
    const now = Date.now();
    localStorage.setItem('lastActivityTime', now.toString());
  },

  // Remove session data
  removeSessionData() {
    localStorage.removeItem('sessionData');
    localStorage.removeItem('sessionStartTime');
    localStorage.removeItem('lastActivityTime');
  },

  // Check if user is authenticated
  isAuthenticated() {
    const sessionData = this.getSessionData();
    return !!sessionData;
  },

  // Check if session has expired based on inactivity
  isSessionExpired() {
    const lastActivityTime = this.getLastActivityTime();
    if (!lastActivityTime) {
      return false;
    }

    const now = Date.now();
    const timeSinceLastActivity = now - parseInt(lastActivityTime);
    
    // For testing: 30 seconds of inactivity (30 seconds)
    // For production: 60 minutes (3600000 milliseconds)
    const INACTIVITY_TIMEOUT = 3600000; // 60 minutes (1 hour)
    
    return timeSinceLastActivity > INACTIVITY_TIMEOUT;
  },

  // Get remaining session time in seconds
  getRemainingSessionTime() {
    const lastActivityTime = this.getLastActivityTime();
    if (!lastActivityTime) return 0;

    const now = Date.now();
    const timeSinceLastActivity = now - parseInt(lastActivityTime);
    const INACTIVITY_TIMEOUT = 3600000; // 60 minutes (1 hour)
    
    const remaining = Math.max(0, INACTIVITY_TIMEOUT - timeSinceLastActivity);
    return Math.round(remaining / 1000);
  },

  // Login using Supabase
  async login(email, password) {
    if (!isSupabaseConfigured || !supabase) {
      throw new Error(SUPABASE_CONFIG_ERROR);
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        throw error;
      }

      if (data && data.user) {
        // Store session data for session management
        this.setSessionData({
          user: data.user,
          email: email
        });
        
        // Identify user in Mixpanel
        if (window.mixpanel) {
          window.mixpanel.identify(data.user.id);
          window.mixpanel.people.set({
            $email: email,
            email: email
          });
        }
        
        return {
          user: {
            email: email,
            id: data.user.id
          }
        };
      }
      
      throw new Error('Login failed');
    } catch (error) {
      throw error;
    }
  },

  // Check if Supabase currently has an active session
  async hasActiveSupabaseSession() {
    if (!isSupabaseConfigured || !supabase) {
      return false;
    }

    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      return !error && !!session;
    } catch (error) {
      return false;
    }
  },

  // Logout using Supabase
  async logout() {
    if (!isSupabaseConfigured || !supabase) {
      this.removeSessionData();
      return;
    }

    try {
      await supabase.auth.signOut();
    } catch (error) {
    } finally {
      // Reset Mixpanel user identification
      if (window.mixpanel) {
        window.mixpanel.reset();
      }
      this.removeSessionData();
    }
  },

  // Get user profile
  async getProfile() {
    const sessionData = this.getSessionData();
    if (sessionData?.user?.id) {
      const { data: userByEmail } = await supabase
        .from('USERS')
        .select('email, cefr_level, class_id')
        .eq('email', sessionData.email)
        .single();

      return {
        email: sessionData.email,
        id: sessionData.user.id,
        cefr_level: userByEmail?.cefr_level ?? '',
        class_id: userByEmail?.class_id ?? null
      };
    }

    if (!isSupabaseConfigured || !supabase) {
      throw new Error('No session data found');
    }

    const { data: { user }, error } = await supabase.auth.getUser();
    if (error || !user) {
      throw new Error('No session data found');
    }

    this.setSessionData({
      user,
      email: user.email || ''
    });

    const { data: userByEmail } = await supabase
      .from('USERS')
      .select('cefr_level, class_id')
      .eq('email', user.email)
      .single();

    return {
      email: user.email,
      id: user.id,
      cefr_level: userByEmail?.cefr_level ?? '',
      class_id: userByEmail?.class_id ?? null
    };
  },

  // Check if session is still valid with Supabase
  async validateSession() {
    if (!isSupabaseConfigured || !supabase) {
      this.removeSessionData();
      return false;
    }

    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session) {
        this.removeSessionData();
        return false;
      }

      // Ensure local session metadata exists for inactivity tracking
      if (!this.getSessionData()) {
        this.setSessionData({
          user: session.user,
          email: session.user?.email || ''
        });
      }

      // Also check if session has expired due to inactivity
      if (this.isSessionExpired()) {
        this.removeSessionData();
        return false;
      }

      return true;
    } catch (error) {
      this.removeSessionData();
      return false;
    }
  }
};

export default hybridAuthService;
